import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface OvertimeRequest {
  id: string;
  tenant_id: string;
  employee_id: string;
  date: string;
  hours_requested: number;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOvertimeRequestInput {
  date: string;
  hours_requested: number;
  reason?: string | null;
}

export interface ReviewOvertimeRequestInput {
  id: string;
  status: 'approved' | 'rejected';
}

export interface OvertimeSummary {
  pending: number;
  approved: number;
  rejected: number;
}

export const overtimeRequestsKeys = {
  all: ['overtime-requests'] as const,
  list: (tenantId: string, status?: string) =>
    [...overtimeRequestsKeys.all, 'list', tenantId, status ?? 'all'] as const,
  summary: (tenantId: string) => [...overtimeRequestsKeys.all, 'summary', tenantId] as const,
} as const;

export function useOvertimeRequests(
  supabase: SupabaseClientLike,
  tenantId: string,
  filters?: { status?: string },
) {
  return useQuery({
    queryKey: overtimeRequestsKeys.list(tenantId, filters?.status),
    queryFn: async () => {
      let q = (supabase as unknown as { from: (t: string) => any })
        .from('overtime_requests')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        q = q.eq('status', filters.status);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data as OvertimeRequest[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateOvertimeRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateOvertimeRequestInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('overtime_requests')
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as OvertimeRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: overtimeRequestsKeys.all }),
  });
}

export function useReviewOvertimeRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: ReviewOvertimeRequestInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('overtime_requests')
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as OvertimeRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: overtimeRequestsKeys.all }),
  });
}

export function useDeleteOvertimeRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('overtime_requests')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: overtimeRequestsKeys.all }),
  });
}

export function useOvertimeSummary(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: overtimeRequestsKeys.summary(tenantId),
    queryFn: async () => {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);

      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('overtime_requests')
        .select('status')
        .eq('tenant_id', tenantId)
        .gte('date', monthStart)
        .lte('date', monthEnd);
      if (error) throw error;

      const rows = (data ?? []) as Array<{ status: string }>;
      return {
        pending: rows.filter((r) => r.status === 'pending').length,
        approved: rows.filter((r) => r.status === 'approved').length,
        rejected: rows.filter((r) => r.status === 'rejected').length,
      } satisfies OvertimeSummary;
    },
    enabled: Boolean(tenantId),
  });
}
